/**
 * @zendir/ui - ObjectInventoryPanel
 *
 * Themed list of simulation objects, grouped by `kind` (spacecraft /
 * ground stations / celestial bodies). Pair with `useSimulationScene`:
 *
 * ```tsx
 * const scene = useSimulationScene({ client, containerId, simulationId });
 * <ObjectInventoryPanel
 *   objects={scene.objects}
 *   onSelect={(obj) => zenRef.current?.focusOn(obj.id, {
 *     zoom: recommendedFlyDistance(obj.kind),
 *   })}
 * />
 * ```
 *
 * The panel renders the toggle UI only — it has no opinion about WHAT
 * happens on select. The consumer wires `onSelect` to its 3D viewer's
 * imperative handle (or anywhere else — the panel is reusable in any
 * dashboard or debug context).
 */

import { memo, useCallback, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useThemeTokens } from "../theme/ThemeProvider";
import { useSpatialSelection } from "../context/SpatialSelectionContext";
import type {
  SimulationSceneObject,
  StructureDiag,
  PositionTickDiag,
} from "../hooks/useSimulationScene";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ObjectInventoryKind = SimulationSceneObject["kind"];

export interface ObjectInventoryPanelProps {
  /** Objects to list. Typically `useSimulationScene().objects`. */
  objects: SimulationSceneObject[];
  /** Click handler — fires with the clicked object. Pure forward; the
   *  panel does not navigate, focus, or fly anywhere itself. */
  onSelect?: (object: SimulationSceneObject) => void;
  /** Currently selected object id (highlights the row). Pass `null` for none. */
  selectedId?: string | null;

  /** Where to anchor the panel on its parent. Default: `top-left`. */
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  /** Pixel offsets from the anchor. Defaults to (16, 56) so a top bar fits above. */
  offset?: { x?: number; y?: number };
  /** Hide the panel without unmounting (preserves `selectedId` state). */
  hidden?: boolean;
  /** Width in pixels. Default 330. */
  width?: number;
  /** Maximum height (CSS value). Default `'50vh'`. */
  maxHeight?: string | number;

  /** Allow the user to collapse / expand the panel. Default true. */
  collapsible?: boolean;
  /** Initial collapsed state. Default false. */
  defaultCollapsed?: boolean;

  /** Optional title override. Default: `"API Objects · N"`. */
  title?: ReactNode;

  /**
   * Optional structure diagnostic block (raw keys + counts from
   * getSimulationStructure). Useful in dev / debug builds; omit in
   * production for a cleaner look.
   */
  structureDiag?: StructureDiag | null;
  /** Optional position-tick diagnostic block. */
  positionTickDiag?: PositionTickDiag | null;

  /** Custom labels per kind (defaults: "Spacecraft", "Ground Stations", "Celestial Bodies"). */
  kindLabels?: Partial<Record<ObjectInventoryKind, string>>;

  /** Inline style override on the outer container. */
  style?: CSSProperties;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Stable short-form of a GUID for inline display. */
function shortId(id: string): string {
  return id.length <= 8 ? id : id.slice(0, 8);
}

const DEFAULT_KIND_LABELS: Record<ObjectInventoryKind, string> = {
  spacecraft: "Spacecraft",
  groundStation: "Ground Stations",
  celestialBody: "Celestial Bodies",
};

const KIND_ORDER: ObjectInventoryKind[] = [
  "spacecraft",
  "groundStation",
  "celestialBody",
];

// ─── Component ────────────────────────────────────────────────────────────────

export const ObjectInventoryPanel = memo(function ObjectInventoryPanel({
  objects,
  onSelect,
  selectedId,
  position = "top-left",
  offset,
  hidden = false,
  width = 330,
  maxHeight = "50vh",
  collapsible = true,
  defaultCollapsed = false,
  title,
  structureDiag,
  positionTickDiag,
  kindLabels,
  style,
}: ObjectInventoryPanelProps) {
  const tokens = useThemeTokens();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // Optional <SpatialSelectionProvider> integration. When present, the
  // panel uses the shared selection state so other components (3D
  // viewer, GroundTrackMap, etc.) stay synchronised. Explicit props
  // always win — consumers can still override per-instance.
  const ctx = useSpatialSelection();
  const effectiveSelectedId =
    selectedId !== undefined ? selectedId : (ctx?.selectedId ?? null);
  const effectiveOnSelect = useCallback(
    (obj: SimulationSceneObject) => {
      onSelect?.(obj);
      // Also push into the shared context if no explicit onSelect wins it.
      if (!onSelect) ctx?.setSelectedId(obj.id);
    },
    [onSelect, ctx],
  );

  const labels = { ...DEFAULT_KIND_LABELS, ...kindLabels };
  const entries = Array.isArray(objects) ? objects : [];

  // Position anchor — translate the prop into top/left/right/bottom CSS.
  const offX = offset?.x ?? 16;
  const offY = offset?.y ?? 56;
  const anchor: CSSProperties = (() => {
    switch (position) {
      case "top-right":
        return { top: offY, right: offX };
      case "bottom-left":
        return { bottom: offY, left: offX };
      case "bottom-right":
        return { bottom: offY, right: offX };
      case "top-left":
      default:
        return { top: offY, left: offX };
    }
  })();

  if (hidden) return null;

  const dim: CSSProperties = { opacity: 0.55, fontSize: "0.9em" };

  return (
    <div
      data-testid="zendir-object-inventory"
      style={{
        position: "absolute",
        zIndex: 2,
        width,
        maxHeight,
        overflow: "auto",
        padding: tokens.spacing.sm,
        borderRadius: tokens.borderRadius?.md ?? 6,
        border: "1px solid rgba(255,255,255,0.12)",
        background: "rgba(0,0,0,0.72)",
        color: tokens.colors.text.primary,
        fontSize: tokens.typography.fontSize.xs,
        lineHeight: 1.35,
        ...anchor,
        ...style,
      }}
    >
      <PanelHeader
        title={title ?? `API Objects · ${entries.length}`}
        collapsible={collapsible}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
      />

      {!collapsed && (
        <>
          {KIND_ORDER.map((kind) => {
            const items = entries.filter((o) => o.kind === kind);
            return (
              <ObjectInventoryGroup
                key={kind}
                title={`${labels[kind]} (${items.length})`}
                items={items}
                selectedId={effectiveSelectedId}
                onSelect={
                  onSelect != null
                    ? onSelect
                    : ctx
                      ? effectiveOnSelect
                      : undefined
                }
              />
            );
          })}

          {structureDiag !== undefined && (
            <StructureDiagBlock diag={structureDiag} dim={dim} />
          )}

          {positionTickDiag !== undefined && (
            <PositionTickDiagBlock diag={positionTickDiag} dim={dim} />
          )}
        </>
      )}
    </div>
  );
});

// ─── Sub-components ───────────────────────────────────────────────────────────

const PanelHeader = ({
  title,
  collapsible,
  collapsed,
  onToggle,
}: {
  title: ReactNode;
  collapsible: boolean;
  collapsed: boolean;
  onToggle: () => void;
}) => {
  if (!collapsible) {
    return <div style={{ marginBottom: 6, fontWeight: 600 }}>{title}</div>;
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 6,
        padding: 0,
        background: "none",
        border: "none",
        color: "inherit",
        font: "inherit",
        fontWeight: 600,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span>{title}</span>
      <span aria-hidden style={{ opacity: 0.7, fontSize: "0.85em" }}>
        {collapsed ? "▸" : "▾"}
      </span>
    </button>
  );
};

const ObjectInventoryGroup = ({
  title,
  items,
  selectedId,
  onSelect,
}: {
  title: string;
  items: SimulationSceneObject[];
  selectedId: string | null;
  onSelect?: (obj: SimulationSceneObject) => void;
}) => (
  <div style={{ marginBottom: 6 }}>
    <div style={{ opacity: 0.85 }}>{title}</div>
    {items.length === 0 ? (
      <div style={{ opacity: 0.6 }}>—</div>
    ) : (
      items.map((item) => (
        <ObjectRow
          key={item.id}
          object={item}
          selected={item.id === selectedId}
          onSelect={onSelect}
        />
      ))
    )}
  </div>
);

const ObjectRow = ({
  object,
  selected,
  onSelect,
}: {
  object: SimulationSceneObject;
  selected: boolean;
  onSelect?: (obj: SimulationSceneObject) => void;
}) => {
  const handleClick = useCallback(() => onSelect?.(object), [onSelect, object]);
  const interactive = onSelect != null;
  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      style={{
        opacity: 0.95,
        padding: "1px 4px",
        borderRadius: 3,
        cursor: interactive ? "pointer" : "default",
        background: selected ? "rgba(255,255,255,0.12)" : "transparent",
      }}
    >
      {object.name} <span style={{ opacity: 0.6 }}>({shortId(object.id)})</span>
    </div>
  );
};

const StructureDiagBlock = ({
  diag,
  dim,
}: {
  diag: StructureDiag | null | undefined;
  dim: CSSProperties;
}) => (
  <div
    style={{
      marginTop: 8,
      borderTop: "1px solid rgba(255,255,255,0.1)",
      paddingTop: 6,
    }}
  >
    {diag ? (
      <>
        <div style={{ ...dim, marginBottom: 2 }}>Raw structure keys:</div>
        <div style={dim}>{diag.topLevelKeys.join(", ") || "—"}</div>
        <div style={{ ...dim, marginTop: 2 }}>
          Objects[]: {diag.rawObjectCount < 0 ? "absent" : diag.rawObjectCount}
          {" · "}
          Systems[]: {diag.rawSystemCount < 0 ? "absent" : diag.rawSystemCount}
        </div>
      </>
    ) : (
      <div style={{ ...dim, marginTop: 4 }}>Waiting for structure…</div>
    )}
  </div>
);

const PositionTickDiagBlock = ({
  diag,
  dim,
}: {
  diag: PositionTickDiag | null | undefined;
  dim: CSSProperties;
}) => {
  if (!diag) return null;
  return (
    <div
      style={{
        marginTop: 8,
        borderTop: "1px solid rgba(255,255,255,0.1)",
        paddingTop: 6,
      }}
    >
      <div style={{ ...dim, marginBottom: 2 }}>Last position tick:</div>
      <div style={dim}>
        ok {diag.parsedOk}/{diag.requested}
        {diag.parseFailed > 0 && ` · parseFail ${diag.parseFailed}`}
        {diag.requestFailed > 0 && ` · reqFail ${diag.requestFailed}`}
      </div>
      <div style={dim}>
        asked for:{" "}
        {diag.requestedProps
          ? diag.requestedProps.join(", ")
          : "getAllProperties (fallback)"}
      </div>
      {diag.firstResponseKeys.length > 0 && (
        <div style={{ ...dim, wordBreak: "break-all" }}>
          got keys: {diag.firstResponseKeys.slice(0, 12).join(", ")}
          {diag.firstResponseKeys.length > 12 && " …"}
        </div>
      )}
      {diag.firstResponseSample && (
        <div style={{ ...dim, wordBreak: "break-all" }}>
          {diag.firstResponseSample}
        </div>
      )}
    </div>
  );
};

// ─── Companion helper ─────────────────────────────────────────────────────────

/**
 * Sensible default fly-to distance per object kind, for use with
 * `<ZenSpace3D />`'s imperative `focusOn(id, { zoom })` handle.
 *
 *   spacecraft:      2 000 000 m  (≈ 2 000 km — close orbital view)
 *   groundStation:   1 500 000 m  (≈ 1 500 km — high-altitude angled view)
 *   celestialBody:  50 000 000 m  (≈ 50 000 km — full-planet view)
 *
 * Override per call if the consumer wants tighter/looser framing.
 */
export function recommendedFlyDistance(kind: ObjectInventoryKind): number {
  switch (kind) {
    case "spacecraft":
      return 2_000_000;
    case "groundStation":
      return 1_500_000;
    case "celestialBody":
      return 50_000_000;
  }
}

export default ObjectInventoryPanel;
